import { z } from 'zod';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';
import {
  FACADE_SUBCATEGORIES,
  COUNTERTOP_SUBCATEGORIES,
  PANEL_SUBCATEGORIES,
  type FacadeSubcategory,
  type CountertopSubcategory,
  type PanelSubcategory,
} from '../domain/decor-subcategories';

/**
 * Контракты Блока 2 «Подбери декор» (PRD `docs/prd/v4/04-block-2-decor.md`).
 *
 * Соответствие источникам:
 *   - xlsx-канон `docs/operations/decors-base-schema.md` — иерархия kind /
 *     subcategory / brand / type. Основа enum'ов.
 *   - PRD §5 — UX/копирайт экранов, эндпоинты `/api/decors/*`.
 *   - P-033 расширена до 3 kind'ов (facade/countertop/panel) 04.05.2026.
 *
 * P-033 enforcement:
 *   - FacadeSchema / CountertopSchema / PanelSchema — раздельные;
 *   - FacadeFilterSchema / CountertopFilterSchema / PanelFilterSchema — раздельные;
 *   - DecorSchema = z.discriminatedUnion('kind', [...]) — это **type-level
 *     declaration** (модель данных), допускается P-033 (см. fixture
 *     allowed-decor-model.ts в verify-тесте ESLint правила).
 *
 * P-001 enforcement:
 *   - globalId: z.string().min(1) — **никаких regex / transform / normalize**.
 *     Спецсимволы `/`, `(`, `)`, пробелы (включая двойные), кириллица — as is.
 *
 * TODO(Phase 6, DecorCard): проверить против PRD §5 Экран 2.2.7 что бордовый
 * бейдж (#8B1538) корректно отображает `globalId` полностью (например
 * "Авокадо глянец_ПВХ_Глянец_AIF_0015"). Если PRD требует короткий человеческий
 * код — добавить `displayCode?: string` как non-breaking optional field.
 * Спросить у владельца продукта при работе над Phase 6.
 */

// ── Дискриминатор kind ────────────────────────────────────────────────────
// 3 равноправных значения после расширения P-033 (xlsx Лист «Корпус (ЛДСП)»
// добавил `panel`). См. CHANGELOG 2026-05-04.
export const DecorKindSchema = z.enum(['facade', 'countertop', 'panel']);
export type DecorKind = z.infer<typeof DecorKindSchema>;

// ── Subcategory enum'ы (раздельно, P-033) ─────────────────────────────────
// Значения берутся из vocabulary (`domain/decor-subcategories.ts`), Zod
// валидирует строки против enum.

const facadeSubcategoryValues = FACADE_SUBCATEGORIES as unknown as [
  FacadeSubcategory,
  ...FacadeSubcategory[],
];
export const FacadeSubcategorySchema = z.enum(facadeSubcategoryValues);

const countertopSubcategoryValues = COUNTERTOP_SUBCATEGORIES as unknown as [
  CountertopSubcategory,
  ...CountertopSubcategory[],
];
export const CountertopSubcategorySchema = z.enum(countertopSubcategoryValues);

const panelSubcategoryValues = PANEL_SUBCATEGORIES as unknown as [
  PanelSubcategory,
  ...PanelSubcategory[],
];
export const PanelSubcategorySchema = z.enum(panelSubcategoryValues);

// ── LabColor (общий для dominantLab декора и DecorMatchRequest) ──────────
// CIE Lab D65: l ∈ [0, 100], a/b ∈ [-128, 127]. См. ADR-0006 / ADR-0009.
export const LabColorSchema = z.object({
  l: z.number().min(0).max(100),
  a: z.number().min(-128).max(127),
  b: z.number().min(-128).max(127),
});
export type LabColor = z.infer<typeof LabColorSchema>;

// ── Per-kind Decor schemas ────────────────────────────────────────────────
// Общие поля. Brand — `z.string()` потому что vocabulary словари меняются
// быстрее чем хочется ронять Zod валидацию (адаптеры импорта Phase 3 сначала
// нормализуют через `normalize*Type`, потом валидируют). Vocabulary —
// источник истины для UI-фильтров и аналитики.
//
// `type` — НЕ в base: задаётся пер-kind (P-033, ADR-0047). Facade/Panel —
// одиночный `string` (один тип на SKU). Countertop — `string[]`: CSV-источники
// (Slotex/Caesarstone/K-Stone) хранят несколько типов на SKU («Крапинка, Бетон,
// Песок»); разбор запятой живёт в адаптерах импорта, не в рантайме фильтра.
const decorBaseFields = {
  globalId: z.string().min(1), // P-001: verbatim
  name: z.string().min(1),
  brand: z.string().min(1),
  imageFilename: z.string().min(1),
  dominantLab: LabColorSchema.optional(), // вычисляется в Phase 3 импорте
  // ADR-0095 A2: короткий алиас карточки `<prefix><share_hash>`, навешивается на
  // API-границе (edge/MSW), optional/аддитивно. См. `packages/shared/src/share/short-id.ts`.
  shortId: z.string().optional(),
};

export const FacadeSchema = z.object({
  ...decorBaseFields,
  kind: z.literal('facade'),
  subcategory: FacadeSubcategorySchema,
  type: z.string().min(1), // одиночный тип (P-033, ADR-0047)
});
export type Facade = z.infer<typeof FacadeSchema>;

export const CountertopSchema = z.object({
  ...decorBaseFields,
  kind: z.literal('countertop'),
  subcategory: CountertopSubcategorySchema,
  type: z.array(z.string()).min(1), // мульти-тип per SKU (B-021, ADR-0047)
});
export type Countertop = z.infer<typeof CountertopSchema>;

export const PanelSchema = z.object({
  ...decorBaseFields,
  kind: z.literal('panel'),
  subcategory: PanelSubcategorySchema,
  type: z.string().min(1), // одиночный тип (P-033, ADR-0047)
});
export type Panel = z.infer<typeof PanelSchema>;

// ── Discriminated union (модель данных — допустимо по P-033) ──────────────
export const DecorSchema = z.discriminatedUnion('kind', [
  FacadeSchema,
  CountertopSchema,
  PanelSchema,
]);
export type Decor = z.infer<typeof DecorSchema>;

// ── Облегчённая item-схема для list endpoint ─────────────────────────────
// Phase 6 retrofit (05.05.2026): добавлен `dominantLab` (≈ 24 байта на запись),
// чтобы color swatch в каталоге рендерился реальным цветом, а не серым
// dashed (B-022 fallback). Без этого list возвращал свотчи серыми на 100%
// карточек — UX-регрессия, превращающая блок «Декор» в серую сетку.
// Изменение backwards-compatible (optional field). Phase 1 решение «лёгкая
// схема» оказалось неоптимальным для UX каталога.
export const DecorListItemSchema = z.object({
  globalId: z.string().min(1),
  name: z.string().min(1),
  kind: DecorKindSchema,
  subcategory: z.string(), // в list не тайпим жёстко по kind — это generic display
  brand: z.string().min(1),
  // Вариант B (ADR-0047): generic list-DTO унифицирован в массив. Facade/Panel
  // проецируются как одноэлементный `[type]`, Countertop — как есть. UI рендерит
  // единым `type.map(...)` без Array.isArray-ветвлений.
  type: z.array(z.string()).min(1),
  primaryPhotoKey: z.string().nullable(),
  dominantLab: LabColorSchema.optional(),
});
export type DecorListItem = z.infer<typeof DecorListItemSchema>;

export const DecorListResponseSchema = PaginatedResponseSchema(DecorListItemSchema);
export type DecorListResponse = z.infer<typeof DecorListResponseSchema>;

// ── Facets (опции фильтра ИЗ ДАННЫХ, ADR-0054) ───────────────────────────
// Опции Бренд/Тип и непустые Подкатегории фасета берутся из фактических
// значений в данных kind, а НЕ из статичных словарей (`*_BRANDS`/`*_DECOR_TYPES`),
// которые разошлись с verbatim-значениями из CSV (K-stone≠K-Stone, и т.п.) и не
// скоупились под подкатегорию → выбор «мёртвого» чипа давал 0 результатов.
//   - `subcategories` — непустые подкатегории kind с количеством SKU (из ПОЛНОГО
//     пула kind, без скоупа), чтобы показывать только то, где есть данные.
//   - `brands`/`types` — distinct-значения, суженные выбранными `subcategories`
//     (скоуп фасета под подкатегорию). `types` для countertop — flatten массива.
export const DecorSubcategoryFacetSchema = z.object({
  value: z.string().min(1),
  count: z.number().int().nonnegative(),
});
export type DecorSubcategoryFacet = z.infer<typeof DecorSubcategoryFacetSchema>;

export const DecorFacetsResponseSchema = z.object({
  subcategories: z.array(DecorSubcategoryFacetSchema),
  brands: z.array(z.string()),
  types: z.array(z.string()),
});
export type DecorFacetsResponse = z.infer<typeof DecorFacetsResponseSchema>;

// ── Per-kind Filter schemas (P-033 — раздельные функции и типы) ──────────
// Сами фильтр-функции — `filterFacades`/`filterCountertops`/`filterPanels` —
// будут реализованы в Phase 2 (раздельно, в `domain/filter-*.ts`).
export const FacadeFilterSchema = z.object({
  subcategories: z.array(FacadeSubcategorySchema).optional(),
  brands: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
});
export type FacadeFilter = z.infer<typeof FacadeFilterSchema>;

export const CountertopFilterSchema = z.object({
  subcategories: z.array(CountertopSubcategorySchema).optional(),
  brands: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
});
export type CountertopFilter = z.infer<typeof CountertopFilterSchema>;

export const PanelFilterSchema = z.object({
  subcategories: z.array(PanelSubcategorySchema).optional(),
  brands: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
});
export type PanelFilter = z.infer<typeof PanelFilterSchema>;

// ── Поиск по названию (ADR-0067) ─────────────────────────────────────────
// Подстрочный поиск по name+brand. Лежит на слое ListQuery, а НЕ в
// *FilterSchema: filter-схемы P-033-чувствительны (per-kind vocabularies
// subcategory/type), а `name` — общее base-поле без kind-ветвления.
// `z.string().optional()` БЕЗ .min(1) намеренно: `GET /api/decors/:kind`
// делит каталог (без name); `name=''` НЕ должен ронять общий list 400-ом —
// матчер (search-decors.ts, Фаза 2) трактует blank как no-op. Мин. длина 2 —
// забота клиента (не шлёт запрос), не контракта.
export const DecorNameQuerySchema = z.object({
  name: z.string().optional(),
});

// ── Query schemas для list endpoints (filter + pagination + name) ────────
export const FacadeListQuerySchema =
  FacadeFilterSchema.merge(PaginationQuerySchema).merge(DecorNameQuerySchema);
export const CountertopListQuerySchema =
  CountertopFilterSchema.merge(PaginationQuerySchema).merge(DecorNameQuerySchema);
export const PanelListQuerySchema =
  PanelFilterSchema.merge(PaginationQuerySchema).merge(DecorNameQuerySchema);

export type FacadeListQuery = z.infer<typeof FacadeListQuerySchema>;
export type CountertopListQuery = z.infer<typeof CountertopListQuerySchema>;
export type PanelListQuery = z.infer<typeof PanelListQuerySchema>;

// ── Alternative mode (Экран 2.2.8 — Светлее/Темнее/Теплее/Холоднее) ──────
// PRD §5: GET /api/decors/{code}/alternatives?mode=lighter|darker|warmer|cooler
export const AlternativeModeSchema = z.enum(['lighter', 'darker', 'warmer', 'cooler']);
export type AlternativeMode = z.infer<typeof AlternativeModeSchema>;
